// #203 / #380 / #386 — emergency manual rides.
//
// A competitor shows up in the ring who isn't on the FVD day sheet. The scribe
// can't be blocked, so the ride is written IMMEDIATELY (offline-first, never
// awaits the network) with a temporary id, and scored right away. The write is
// mirrored two ways: a synchronous localStorage list (this module's fast read
// path, used in render) AND the IndexedDB rides cache + outbox (#422), so the
// sync engine POSTs it to score-api on connectivity, stamps the returned
// serverId back here (via syncEngine.onRideSynced → setManualRideServerId), and
// remaps the queued ScoreSheet — so score-admin sees the ride (needsReview) and
// can attach the real horse/rider.

import {
  enqueue,
  peekOutbox,
  removeOutboxOpsUnlessInFlight,
  rides as ridesCache,
  scoreSheets as scoreSheetsCache,
} from "../local/db";
import { syncEngine } from "../local/sync";

const STORAGE_KEY = "scribe-fe.manualRides";

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// #882 — the localStorage list IS the UI's source of truth for manual rides
// (readAll/writeAll below), so anything that mutates it needs to be able to
// notify listeners synchronously — this is how the "N unsynced rides"
// indicator (SessionHeader) stays live without polling.
const changeListeners = new Set();
function notifyChange() {
  const list = readAll();
  for (const fn of changeListeners) fn(list);
}

// Subscribe to the manual-rides list. Fires immediately with the current
// list, then on every write (add, serverId stamp, queue-error flag).
export function subscribeManualRides(fn) {
  changeListeners.add(fn);
  fn(readAll());
  return () => changeListeners.delete(fn);
}

// A ride counts as "unsynced" until its createRide op has actually reached
// score-api and stamped a serverId back (setManualRideServerId). This is
// deliberately NOT derived from the IndexedDB outbox: that's exactly the
// thing that can silently fail to receive the op in the first place (see
// cacheAndQueue below) — the localStorage mirror is the one place that
// can't lie about "did this ride ever get a server id".
export function getUnsyncedManualRideCount() {
  return readAll().filter((r) => !r.serverId).length;
}

function writeAll(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage unavailable (private browsing) — non-fatal.
  }
  notifyChange();
}

export function getManualRidesForClass(classId) {
  if (!classId) return [];
  return readAll().filter((r) => r.classId === classId);
}

// Resolve by the temp id OR the assigned serverId, so a route/scoresheet that
// references either still finds the ride after it syncs.
export function getManualRideById(id) {
  if (!id) return null;
  return readAll().find((r) => r.id === id || r.serverId === id) ?? null;
}

// Stamp the score-api id onto a synced ride (keeps the temp id as the local
// key so open scoring screens keep resolving it).
export function setManualRideServerId(tempId, serverId) {
  const list = readAll();
  const row = list.find((r) => r.id === tempId);
  if (row) {
    row.serverId = serverId;
    row.needsReview = true;
    writeAll(list);
  }
}

let _counter = 0;
const nextId = (classId) =>
  `manual-${classId}-${Date.now().toString(36)}-${++_counter}`;

// competitorNumber (CNO off the bridle tag) is the primary identifier; the
// scribe may have only that. testId/horse/rider are optional.
//
// #1286 — showId (the session's selected show) and scheduledTime are REQUIRED,
// not optional extras. `GET /ride?showId=` (#637) is a Query on the
// `ridesByShow` GSI whose HASH key is showId, and it's what the announcer's
// running order is built on — a ride with no showId cannot come back from
// that call at all, so the announcer never sees a rider who is standing in
// the ring being scored right now. scheduledTime defaults to "now": the horse
// is in the ring, so "now" is the only time that means anything for a ride
// with no day-sheet slot, and it's what puts the ride in the right place in a
// day-narrowed (`begins_with(scheduledTime, day)`) running-order query.
export function addManualRide(classId, { competitorNumber, testId, horse, rider, showId }) {
  if (!classId) return null;
  const cno = (competitorNumber ?? "").toString().trim();
  const ride = {
    id: nextId(classId),
    serverId: null,
    classId,
    showId: showId || null,
    testId: testId || null,
    competitorNumber: cno || null,
    // rideNumber kept numeric for running-order display when the CNO is numeric.
    rideNumber: Number.isFinite(Number(cno)) && cno ? Number(cno) : null,
    horseId: null,
    riderId: null,
    horseName: (horse ?? "").trim() || "—",
    riderName: (rider ?? "").trim() || "—",
    scheduledTime: new Date().toISOString(),
    status: "PENDING",
    isManual: true,
    needsReview: true,
    createdAt: new Date().toISOString(),
  };
  const list = readAll();
  list.push(ride);
  writeAll(list);
  // Mirror into the local-first cache + outbox (#422). Fire-and-forget: the
  // scribe is never blocked on IndexedDB or the network.
  void cacheAndQueue(ride);
  return ride;
}

// score-api createRide body for a manual ride (omit the "—" name placeholders).
function toCreateRideBody(ride) {
  return {
    classId: ride.classId,
    showId: ride.showId ?? undefined,
    scheduledTime: ride.scheduledTime ?? undefined,
    testId: ride.testId ?? undefined,
    competitorNumber: ride.competitorNumber ?? undefined,
    horseName:
      ride.horseName && ride.horseName !== "—" ? ride.horseName : undefined,
    riderName:
      ride.riderName && ride.riderName !== "—" ? ride.riderName : undefined,
  };
}

// #882 — a CDS prod incident (2026-07-17, CNO 462) root-caused to this
// function swallowing an IndexedDB/outbox write failure: the localStorage
// row (and hence the UI) looked exactly like a normal saved ride, but the
// createRide op never reached the outbox at all, so the sync engine had
// nothing to drain and no retry ever happened. A ride whose op never queues
// is *worse* than one that queues and fails to POST (sync.js's backoff retry
// only helps once something is actually in the outbox) — so this needs its
// own retry, and it must never fail silently.
const QUEUE_RETRY_BASE_MS = 3000;
const QUEUE_RETRY_MAX_MS = 60000;

async function cacheAndQueue(ride, attempt = 1) {
  try {
    await ridesCache.put(ride);
    await enqueue({
      opId: `createRide:${ride.id}`,
      type: "createRide",
      entityKey: ride.id,
      tempId: ride.id,
      payload: toCreateRideBody(ride),
    });
    if (syncEngine.isOnline()) await syncEngine.flush();
    else await syncEngine.refreshPending();
  } catch (err) {
    // Surface it loudly — this kiosk has no telemetry pipe, so the browser
    // console is the only trail an admin/dev has after the fact — and retry
    // with backoff rather than dropping the write on the floor. Bail if the
    // ride has since synced (serverId stamped) or been removed from the
    // local list some other way.
    console.error(
      `scribe-fe: manual ride "${ride.id}" failed to queue for sync (attempt ${attempt}); retrying`,
      err,
    );
    const delay = Math.min(
      QUEUE_RETRY_MAX_MS,
      QUEUE_RETRY_BASE_MS * 2 ** (attempt - 1),
    );
    setTimeout(() => {
      const current = readAll().find((r) => r.id === ride.id);
      if (!current || current.serverId) return;
      void cacheAndQueue(current, attempt + 1);
    }, delay);
  }
}

// #1922 — the undo path for a manual ride added by mistake. A manual ride
// can never be scored-and-synced-away like a real one; before this, once
// `addManualRide` ran there was no code path that ever removed it (see the
// module doc above and the card for the CDS incident this reproduces: a
// scribe deleted the outbox `createRide` op by hand to try to clear a
// mistaken add, and the "unsynced" chip stayed lit forever because nothing
// ever touched the localStorage mirror that chip actually reads).
//
// Non-mutating: reports what WOULD happen without doing it, so the UI can
// decide which confirm dialog to show before anything is thrown away.
//   { ok: true, ride }
//   { ok: false, reason: "not-found" }
//   { ok: false, reason: "has-server-id", ride }               — AC5
//   { ok: false, reason: "create-in-flight", ride }            — #2037
//   { ok: false, reason: "has-score-data", ride,
//     scoreSheetCount, queuedOpCount, otherOpIds }              — AC4
async function checkDiscardGuard(tempId) {
  const ride = getManualRideById(tempId);
  if (!ride) return { ok: false, reason: "not-found" };
  // A synced ride exists in score-api now. Removing it locally would only
  // hide it here, not undo it — that needs a real delete-ride flow on the
  // server side, which is out of scope for this kiosk-local action.
  if (ride.serverId) return { ok: false, reason: "has-server-id", ride };

  const [scoreSheetsForRide, outboxOps] = await Promise.all([
    scoreSheetsCache.forRide(tempId),
    peekOutbox(),
  ]);
  const createRideOpId = `createRide:${tempId}`;
  // #2037 — the createRide POST for this very ride is in flight RIGHT NOW:
  // sync.js stamps the op inFlight before sending and clears it only on
  // failure (success removes the op). serverId is still null in this window,
  // so without this check a discard here would clear all three local stores,
  // the POST would then succeed, and onRideSynced → setManualRideServerId
  // would find no row — a ride on score-api with no local counterpart.
  // REFUSES (never waits — the kiosk must not hang on the network to
  // discard); the scribe retries once the send lands, at which point this
  // becomes a plain "has-server-id" refusal. Not forceable, same as
  // has-server-id: forcing it recreates the exact orphan.
  const createRideOp = outboxOps.find((op) => op.opId === createRideOpId);
  if (createRideOp?.inFlight) {
    return { ok: false, reason: "create-in-flight", ride };
  }
  // Any OTHER queued op for this ride (a score-sheet save, a utility event)
  // means real work happened on it. Score-sheet ops key their entityKey
  // "<rideId>::<judgeId>" (useScoreSheet.js); utility-event ops key it to
  // the bare rideId (useRideUtilityEvents.js) — match both shapes.
  const otherOps = outboxOps.filter(
    (op) =>
      op.opId !== createRideOpId &&
      (op.entityKey === tempId || op.entityKey?.startsWith(`${tempId}::`)),
  );
  if (scoreSheetsForRide.length || otherOps.length) {
    return {
      ok: false,
      reason: "has-score-data",
      ride,
      scoreSheetCount: scoreSheetsForRide.length,
      queuedOpCount: otherOps.length,
      otherOpIds: otherOps.map((op) => op.opId),
    };
  }
  return { ok: true, ride };
}

// Preview for the UI: what discarding this ride right now would do, without
// doing it. Drives which confirm dialog to show (plain vs. "this will also
// lose X" second-tier) before the scribe commits to anything.
export function checkManualRideDiscard(tempId) {
  return checkDiscardGuard(tempId);
}

// Clears a manual ride from all three stores it lives in (module doc above):
// the localStorage "scribe-fe.manualRides" entry, the IndexedDB rides cache
// row, and the outbox "createRide:<tempId>" op — keyed off the ride's temp
// id, in one operation.
//
// Refused per checkDiscardGuard's reasons unless `force: true`, and even
// then only past a "has-score-data" refusal — a "has-server-id",
// "create-in-flight" (#2037) or "not-found" refusal can never be forced
// through. When forced, the OTHER
// queued ops the guard found are removed too, not just createRide: leaving
// a score-sheet op behind whose tempId no longer resolves to any ride would
// wedge sync.js's ordering guard (an op carrying a tempId waits forever for
// a matching createRide op that will never arrive) — recreating this same
// card's bug one op later.
export async function discardManualRide(tempId, { force = false } = {}) {
  const guard = await checkDiscardGuard(tempId);
  if (!guard.ok) {
    const forcible = force && guard.reason === "has-score-data";
    if (!forcible) return guard;
  }

  const createRideOpId = `createRide:${tempId}`;
  const otherOpIds = guard.otherOpIds ?? [];
  // #2037 repair — the outbox delete is ONE readwrite transaction that
  // re-reads the createRide op and REFUSES with "create-in-flight" if the
  // engine stamped it inFlight after checkDiscardGuard's peek above. Doing
  // the delete first (before touching ridesCache/localStorage) means a
  // refusal leaves every store untouched — no half-cleared ride whose POST
  // then lands orphaned on score-api.
  const removal = await removeOutboxOpsUnlessInFlight(createRideOpId, otherOpIds);
  if (!removal.removed) {
    return { ok: false, reason: "create-in-flight", ride: guard.ride };
  }

  await ridesCache.remove(tempId);
  writeAll(readAll().filter((r) => r.id !== tempId));

  return { ok: true, ride: guard.ride };
}
